import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface AdminCheckResult {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Server-side verification of admin/super_admin status.
 * Uses the database has_role() function to prevent client-side manipulation.
 * Results are cached via react-query to avoid redundant RPC calls across components.
 */
export function useAdminCheck(): AdminCheckResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-role-check'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { isAdmin: false, isSuperAdmin: false };
      }

      // Check both roles in parallel
      const [superAdminResult, adminResult] = await Promise.all([
        supabase.rpc('has_role', { _user_id: user.id, _role: 'super_admin' }),
        supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }),
      ]);

      const isSuperAdmin = superAdminResult.data === true;
      const isAdmin = adminResult.data === true;

      return { isAdmin: isAdmin || isSuperAdmin, isSuperAdmin };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes — admin status rarely changes
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  return { 
    isAdmin: data?.isAdmin ?? false,
    isSuperAdmin: data?.isSuperAdmin ?? false,
    isLoading, 
    error: error ? (error instanceof Error ? error.message : 'Failed to verify admin status') : null,
  };
}
